<script lang="ts">
  import type { DateTime } from "./lib/date-time";
  import type { SelectionRange, SelectionState } from "./lib/range";
  import { Availabilities, Possibility } from "./availability/availability-provider";
  import { DatePickerTranslations } from "./DatePicker.i18n";
  import type { PanelDay } from "./lib/calender";

  export let today: DateTime;
  export let day: PanelDay;
  export let selection: SelectionRange;
  export let availabilities: Availabilities;
  export let language: string;

  $: T = DatePickerTranslations(language);

  $: selectionState = selection.getState(day.date);
  $: state = $selectionState;

  $: _dayLabel = dayLabel(day.date, state);
  $: _isValidArrival = isValidArrival(day.date, state);
  $: _isInvalidArrival = isInvalidArrival(day.date, state);
  $: _isValidDiparture = isValidDiparture(day.date, state);
  $: _isInvalidDiparture = isInvalidDiparture(day.date, state);

  function isValidArrival(date: DateTime, state: SelectionState) {
    const { from, complete } = state;

    if (
      !from || // nothing selected jet
      complete || // selection complete -> show possible arrivals!
      from.ticks === date.ticks // the selected arrival day
    ) {
      return availabilities.isArrivalPossible(date) === Possibility.Possible;
    }

    return false;
  }

  function isInvalidArrival(date: DateTime, state: SelectionState) {
    return false;
  }

  function isValidDiparture(date: DateTime, state: SelectionState) {
    const { from } = state;
    return availabilities.isDiparturePossible(from, date) === Possibility.Possible;
  }

  function isInvalidDiparture(date: DateTime, state: SelectionState) {
    const { from, complete } = state;
    return !complete && availabilities.isDiparturePossible(from, date) === Possibility.NotPossible;
  }

  function dayLabel(date: DateTime, state: SelectionState) {
    if (!availabilities.showLabel) {
      return "";
    }

    const { from, complete } = state;

    const arrival = !from || complete;

    if (arrival) {
      const pos = availabilities.isArrivalPossible(date);
      switch (pos) {
        case Possibility.Possible:
          return T.get("arrival.possible");
        default:
          return T.get("arrival.notPossible");
      }
    } else {
      const pos = availabilities.isDiparturePossible(from, date);
      switch (pos) {
        case Possibility.Possible:
          return T.get("departure.possible");
        case Possibility.NotPossible:
          return T.get("departure.mionStay");
        default:
          return T.get("departure.notPossible");
      }
    }
  }

  function selectDate(date: DateTime) {
    const { isDisabled, complete, empty, from, to } = state;

    if (isDisabled) {
      return;
    }

    if (complete || empty) {
      selection.select(date);
      if (selection.minStay > 0) {
        selection.mouseenter(date.addDays(selection.minStay));
      }
    } else if (from && !to && Math.abs(from.subtract(date).days) >= selection.minStay) {
      selection.select(date);
    }
  }
</script>

<div
  class="lrs-day hint hint--top"
  aria-label={_dayLabel}
  class:lrs-prev-month={day.lastMonth}
  class:lrs-day--today={today.ticks === day.date.ticks}
  class:lrs-day--selected-shadow={state.containsShadow}
  class:lrs-day--selected={state.contains}
  class:lrs-day--selected-start={state.isStart}
  class:lrs-day--selected-end={state.isEnd}
  class:lrs-day--selected-single={state.isSingle}
  class:lrs-day--disabled={state.isDisabled}
  class:lrs-day--valid-arrival={_isValidArrival}
  class:lrs-day--valid-diparture={_isValidDiparture}
  class:lrs-day--invalid-arrival={_isInvalidArrival}
  class:lrs-day--invalid-diparture={_isInvalidDiparture}
  on:mouseenter={(_) => selection.mouseenter(day.date)}
  on:mouseleave={(_) => selection.mouseleave(day.date)}
  on:click={(_) => selectDate(day.date)}
>
  <div class="lrs-selection" />

  <div class="lrs-text">
    {day.date.day}
  </div>
</div>

<style lang="scss" global>
  @import "./styles/hint";

  // variables
  //

  $selection-color: #349aef;
  $invalid-color: #f0ad4e;
  $valid-color: #93c763;

  :root {
    --selection-color: #{$selection-color};
    --invalid-color: #{$invalid-color};
    --valid-color: #{$valid-color};

    --selection-color-dark: #{darken($selection-color, 10%)};
    --invalid-color-dark: #{darken($invalid-color, 10%)};
    --valid-color-dark: #{darken($valid-color, 10%)};
  }

  // days
  //

  .lrs-day {
    .lrs-text:after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      right: 2px;
      bottom: 2px;
      border-radius: 50%;
    }

    // today
    &.lrs-day--today .lrs-text:after {
      box-shadow: 0 0 0 2px rgba($invalid-color, 0.5);
      background-color: rgba($invalid-color, 0.08);
    }

    // hover
    &:hover .lrs-text:after {
      box-shadow: 0 0 0 2px var(--invalid-color);
    }

    // // highlight arrival / departure days
    // &.lrs-day--valid-arrival,
    // &.lrs-day--valid-diparture {
    //     background: var(--valid-color);

    //     .lrs-text {
    //         color: #fff;
    //     }

    //     // hover
    //     &:hover .lrs-text:after {
    //         box-shadow: 0 0 0 2px var(--valid-color-dark);
    //     }
    // }

    // &.lrs-day--invalid-arrival,
    // &.lrs-day--invalid-diparture:not(.lrs-day--selected-start) {
    //     background: var(--invalid-color);

    //     .lrs-text {
    //         color: #fff;
    //     }

    //     // hover
    //     &:hover .lrs-text:after {
    //         box-shadow: 0 0 0 2px var(--invalid-color-dark);
    //     }
    // }

    // hover selected
    &.lrs-day--selected:hover .lrs-text:after {
      box-shadow: 0 0 0 2px var(--selection-color-dark) !important;
    }
  }

  // selection
  //

  .lrs-selection {
    background-color: currentColor;

    @at-root .lrs-day--selected & {
      color: var(--selection-color);

      + .lrs-text {
        color: #fff;
      }
    }

    @at-root .lrs-selection-invalid .lrs-day--selected & {
      color: var(--invalid-color);
    }
  }

  // day
  //

  .lrs-day {
    position: relative;
    width: 3em;
    height: 3em;
    cursor: pointer;

    .lrs-text {
      color: inherit;
    }

    .lrs-selection,
    .lrs-text {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.lrs-prev-month {
      opacity: 0.4;
    }

    &.lrs-day--disabled {
      opacity: 0.3;
    }
  }

  // selection
  //

  .lrs-selection {
    background-color: currentColor;
    color: transparent;

    @at-root .lrs-day--selected & {
      // ...
    }

    // @at-root .lrs-day--selected-shadow & {
    //   background-color: rgba(#000, 0.1);
    // }

    @at-root .lrs-day--selected.lrs-day--selected-start & {
      border-top-left-radius: 50%;
      border-bottom-left-radius: 50%;
    }

    @at-root .lrs-day--selected.lrs-day--selected-end & {
      border-top-right-radius: 50%;
      border-bottom-right-radius: 50%;

      &:after {
        content: "";
        position: absolute;
        top: 2px;
        left: 2px;
        right: 2px;
        bottom: 2px;
        border-radius: 50%;
        background-color: #fff;
      }

      + .lrs-text {
        color: inherit !important;
      }
    }

    @at-root .lrs-day--selected.lrs-day--selected-single & {
      border-radius: 50%;
    }
  }
</style>
